import type { ErrorComponentProps } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { useState } from "react";

export function ErrorPane({ error, reset }: ErrorComponentProps) {
  const [showDetails, setShowDetails] = useState(false);
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center px-6 py-8">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="font-serif text-2xl font-semibold tracking-tight">
            Something went wrong
          </h1>
          <p className="text-sm text-muted-foreground">
            Hearth ran into an unexpected error rendering this page. Try again,
            or head home to keep working.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground shadow-sm hover:opacity-90"
          >
            <RefreshCw className="h-4 w-4" />
            Try again
          </button>
          <Link
            to="/"
            search={{ chat: undefined }}
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Home className="h-4 w-4" />
            Back to home
          </Link>
        </div>
        {message ? (
          <div className="flex w-full flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              {showDetails ? "Hide details" : "Show details"}
            </button>
            {showDetails ? (
              <pre className="max-h-64 w-full overflow-auto rounded-md border border-border bg-muted/40 p-3 text-left text-xs text-destructive">
                <code>{stack ?? message}</code>
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
